import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Compass, Heart, Menu, X, Camera, Shirt, User, LogOut, Sparkles, Newspaper, ChevronDown, ImageIcon, Rocket, Palette, Wand2, Book, Calendar, Bot, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { cn } from "./lib/utils";
import AIAssistant from './components/AIAssistant';
import Footer from './components/Footer';
import OfflineBanner from './components/OfflineBanner';
import { cacheClearAll } from './utils/offlineCache';
import { getApiUrl } from './utils';
import { getRefreshToken, clearRefreshToken } from './utils/secureStorage';

// Pinterest icon component
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

// Main navigation items (shown directly)
const mainNavItems = [
    { name: 'My Wardrobe', icon: Shirt, page: 'MyWardrobe', path: '/my-wardrobe' },
    { name: 'Style Studio', icon: Sparkles, page: 'StyleStudio', path: '/style-studio' },
    { name: 'AI Studio', icon: Wand2, page: 'AIStudio', path: '/ai-studio' },
    { name: 'Outfit Calendar', icon: Calendar, page: 'OutfitCalendar', path: '/outfit-calendar' },
    { name: 'Stylebook', icon: Book, page: 'Lookbooks', path: '/lookbooks' },
    { name: 'Picks of the Week', icon: Palette, page: 'StylePicks', path: '/style-picks' },
    { name: 'Promoters', icon: Rocket, page: 'Promoters', path: '/promoters' },
];

// Tools dropdown items
const toolsNavItems = [
    // ARCHIVED: { name: 'Explore', icon: Compass, page: 'Explore', path: '/explore' },
    // ARCHIVED: { name: 'Visual Search', icon: Camera, page: 'VisualSearch', path: '/visual-search' },
    { name: 'Home', icon: Home, page: 'Home', path: '/home' },
    { name: 'Saved', icon: Heart, page: 'Saved', path: '/saved' },
    { name: 'News', icon: Newspaper, page: 'News', path: '/news' },
];

// Mobile menu items - items NOT in bottom tab bar
const mobileMenuItems = [
    { name: 'Outfit Calendar', icon: Calendar, page: 'OutfitCalendar', path: '/outfit-calendar' },
    { name: 'Stylebook', icon: Book, page: 'Lookbooks', path: '/lookbooks' },
    { name: 'Promoters', icon: Rocket, page: 'Promoters', path: '/promoters' },
    { name: 'News', icon: Newspaper, page: 'News', path: '/news' },
    { name: 'Home', icon: Home, page: 'Home', path: '/home' },
];

// All nav items for desktop mobile menu (full list for reference)
const allNavItems = [...mainNavItems, ...toolsNavItems];

export default function Layout({ children, currentPageName }) {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [guideDismissed, setGuideDismissed] = useState(() => localStorage.getItem('guideDismissed') === 'true');
    const [styleMaidDismissed, setStyleMaidDismissed] = useState(() => localStorage.getItem('styleMaidDismissed') === 'true');
    const navigate = useNavigate();
    const location = useLocation();

    // Scroll to top when route changes
    useEffect(() => {
        document.getElementById('root')?.scrollTo(0, 0);
    }, [location.pathname]);

    const handleDismissAgent = (agent) => {
        if (agent === 'guide') {
            setGuideDismissed(true);
            localStorage.setItem('guideDismissed', 'true');
        }
    };

    const handleRestoreAgents = () => {
        setGuideDismissed(false);
        setStyleMaidDismissed(false);
        localStorage.removeItem('guideDismissed');
        localStorage.removeItem('styleMaidDismissed');
        window.dispatchEvent(new CustomEvent('restoreAgents'));
    };

    const anyAgentDismissed = guideDismissed || styleMaidDismissed;

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.tools-dropdown')) {
                setToolsDropdownOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            const refreshToken = await getRefreshToken();
            if (refreshToken) {
                await axios.post(`${getApiUrl()}/auth/logout`, { refreshToken });
            }
        } catch (e) {
            // Best-effort — proceed with local logout regardless
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        await clearRefreshToken();
        cacheClearAll(); // Clear offline cache on logout
        setUser(null);
        navigate('/home');
    };

    const isToolsActive = toolsNavItems.some(item => item.page === currentPageName);

    return (
        <div className="min-h-screen animated-gradient">
            {/* Desktop Navigation */}
            <header className="hidden lg:block fixed top-0 left-0 right-0 z-50">
                <div className="mx-4 mt-4">
                    <div className="glass-card-solid rounded-2xl max-w-7xl mx-auto">
                        <div className="px-6">
                            <div className="flex items-center justify-between h-20">
                                <Link to="/home" className="flex items-center group">
                                    <img 
                                        src="/logo-header.png" 
                                        alt="fashionDisc" 
                                        className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
                                    />
                                </Link>
                                
                                <nav className="flex items-center gap-1 bg-stone-100/60 rounded-xl p-1.5">
                                    {/* Main Nav Items */}
                                    {mainNavItems.map((item) => (
                                        <Link
                                            key={item.page}
                                            to={item.path}
                                            className={cn(
                                                "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2",
                                                currentPageName === item.page
                                                    ? "bg-white text-stone-900 shadow-sm"
                                                    : "text-stone-500 hover:text-stone-900 hover:bg-white/50"
                                            )}
                                        >
                                            <item.icon className="w-4 h-4" />
                                            {item.name}
                                        </Link>
                                    ))}

                                    {/* Tools Dropdown */}
                                    <div className="relative tools-dropdown">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setToolsDropdownOpen(!toolsDropdownOpen);
                                            }}
                                            className={cn(
                                                "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2",
                                                isToolsActive
                                                    ? "bg-white text-stone-900 shadow-sm"
                                                    : "text-stone-500 hover:text-stone-900 hover:bg-white/50"
                                            )}
                                        >
                                            <Shirt className="w-4 h-4" />
                                            Tools
                                            <ChevronDown className={cn(
                                                "w-3.5 h-3.5 transition-transform duration-200",
                                                toolsDropdownOpen && "rotate-180"
                                            )} />
                                        </button>

                                        <AnimatePresence>
                                            {toolsDropdownOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                                                    transition={{ duration: 0.15 }}
                                                    className="absolute top-full right-0 mt-2 w-52 glass-card-solid rounded-xl overflow-hidden py-2"
                                                >
                                                    {toolsNavItems.map((item) => (
                                                        <Link
                                                            key={item.page}
                                                            to={item.path}
                                                            onClick={() => setToolsDropdownOpen(false)}
                                                            className={cn(
                                                                "flex items-center gap-3 px-4 py-2.5 transition-colors",
                                                                currentPageName === item.page
                                                                    ? "bg-stone-100 text-stone-900"
                                                                    : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                                                            )}
                                                        >
                                                            <item.icon className="w-4 h-4" />
                                                            <span className="font-medium">{item.name}</span>
                                                        </Link>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </nav>
                                
                                {/* Divider */}
                                <div className="w-px h-8 bg-stone-200/60 mx-4" />

                                {/* Auth Section */}
                                {user ? (
                                    <div className="flex items-center gap-2">
                                        <Link 
                                            to="/account"
                                            className={cn(
                                                "flex items-center gap-2.5 px-4 py-2 rounded-xl transition-all duration-200",
                                                currentPageName === 'Account'
                                                    ? "bg-stone-900 text-white"
                                                    : "glass-card hover:shadow-md text-stone-700"
                                            )}
                                        >
                                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-terracotta-500 to-suede-600 flex items-center justify-center shadow-sm">
                                                <span className="text-xs font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                                            </div>
                                            <span className="text-sm font-medium">{user.name?.split(' ')[0]}</span>
                                        </Link>
                                        <button
                                            onClick={handleLogout}
                                            className="p-2.5 rounded-xl text-stone-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
                                            title="Logout"
                                        >
                                            <LogOut className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="modern-button-primary"
                                    >
                                        Sign In
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            
            {/* Mobile Navigation */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 pt-safe bg-[#f5ebe0]/90 backdrop-blur-md border-b border-stone-200/40">
                <div className="px-4 pt-1">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/home" className="flex items-center">
                            <img 
                                src="/logo-header.png" 
                                alt="fashionDisc" 
                                className="h-12 w-auto object-contain"
                            />
                        </Link>
                        
                        <div className="flex items-center gap-2">
                            {user && (
                                <Link 
                                    to="/account"
                                    className="w-8 h-8 rounded-full bg-gradient-to-br from-terracotta-500 to-suede-600 flex items-center justify-center shadow-sm"
                                >
                                    <span className="text-xs font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                                </Link>
                            )}
                            <button 
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-2.5 rounded-xl hover:bg-black/5 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                            >
                                {mobileMenuOpen ? (
                                    <X className="w-5 h-5 text-stone-600" />
                                ) : (
                                    <Menu className="w-5 h-5 text-stone-600" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mx-3 mt-2"
                        >
                            <div className="glass-card-solid rounded-2xl p-4">
                                <nav className="space-y-1">
                                    {/* Quick access items not in bottom bar */}
                                    <p className="px-3 py-2 text-xs font-semibold text-stone-400 uppercase tracking-wider">Quick Access</p>
                                    {mobileMenuItems.map((item) => (
                                        <Link
                                            key={item.page}
                                            to={item.path}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={cn(
                                                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                                                currentPageName === item.page
                                                    ? "bg-stone-900 text-white"
                                                    : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                                            )}
                                        >
                                            <item.icon className="w-5 h-5" />
                                            <span className="font-medium">{item.name}</span>
                                        </Link>
                                    ))}
                                    
                                    {/* Mobile Auth Section */}
                                    <div className="border-t border-stone-200/60 pt-4 mt-4">
                                        {user ? (
                                            <div className="space-y-2">
                                                <Link
                                                    to="/account"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className={cn(
                                                        "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                                                        currentPageName === 'Account'
                                                            ? "bg-stone-900 text-white"
                                                            : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                                                    )}
                                                >
                                                    <User className="w-5 h-5" />
                                                    <span className="font-medium">{user.name}</span>
                                                </Link>
                                                <button
                                                    onClick={() => {
                                                        handleLogout();
                                                        setMobileMenuOpen(false);
                                                    }}
                                                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 w-full transition-colors"
                                                >
                                                    <LogOut className="w-5 h-5" />
                                                    <span className="font-medium">Sign Out</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <Link
                                                to="/login"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl modern-button-primary w-full"
                                            >
                                                <User className="w-5 h-5" />
                                                Sign In
                                            </Link>
                                        )}
                                    </div>
                                </nav>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>
            
            {/* Offline Banner */}
            <OfflineBanner />

            {/* Main Content */}
            <main className="min-h-screen pt-[calc(theme(spacing.10)+env(safe-area-inset-top)+8px)] lg:pt-28 pb-0 lg:pb-0">
                {children}
            </main>
            
            {/* Footer - Only visible on Home page */}
            {currentPageName === 'Home' && (
                <div className="pb-24 lg:pb-0">
                    <Footer />
                </div>
            )}
            
            {/* Mobile Bottom Tab Bar - iOS Style: with extended background to cover safe area */}
            <div className="lg:hidden fixed left-0 right-0 bottom-0 z-50">
                {/* Background extension that goes all the way to bottom */}
                <div 
                    className={cn(
                        "absolute inset-0",
                        (currentPageName === 'MyWardrobe' || currentPageName === 'Home') 
                            ? "bg-[#f0e6d8]/30" 
                            : "bg-transparent"
                    )} 
                    style={{ bottom: '-100px' }}
                ></div>
                
                {/* Actual navigation bar */}
                <nav className={cn(
                    "relative border-t border-stone-300/40",
                    (currentPageName === 'MyWardrobe' || currentPageName === 'Home')
                        ? "bg-[#f0e6d8]/30 backdrop-blur-sm"
                        : "bg-transparent"
                )}>
                    <div className="flex items-center justify-around px-2 h-[84px]">
                    {[
                        { name: 'Style Studio', icon: Sparkles, page: 'StyleStudio', path: '/style-studio', center: false },
                        { name: 'AI Studio', icon: Wand2, page: 'AIStudio', path: '/ai-studio', center: false },
                        { name: 'Wardrobe', icon: Shirt, page: 'MyWardrobe', path: '/my-wardrobe', center: true },
                        { name: 'Saved', icon: Heart, page: 'Saved', path: '/saved', center: false },
                        { name: 'Picks', icon: Palette, page: 'StylePicks', path: '/style-picks', center: false },
                    ].map((item) => (
                        <a
                            key={item.page}
                            href={item.path}
                            onClick={(e) => {
                                e.preventDefault();
                                // If clicking on the currently active tab (except Home), force reload to main page
                                if (currentPageName === item.page && item.page !== 'Home') {
                                    window.location.href = item.path;
                                } else {
                                    navigate(item.path);
                                }
                            }}
                            style={{ pointerEvents: 'auto', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                            className={cn(
                                "flex flex-col items-center justify-center min-w-[56px] transition-all duration-200",
                                item.center ? "relative -mt-3" : ""
                            )}
                        >
                                    {item.center ? (
                                        /* Elevated center wardrobe button */
                                        <>
                                            <div className={cn(
                                                "w-[66px] h-[66px] rounded-full flex items-center justify-center shadow-lg transition-all border-2 border-stone-300/50",
                                                currentPageName === item.page
                                                    ? "bg-gradient-to-br from-terracotta-500 to-orange-500 shadow-orange-500/30"
                                                    : "bg-gradient-to-br from-terracotta-400 to-orange-400 shadow-orange-400/20"
                                            )}>
                                                <item.icon className="w-8 h-8 text-white" />
                                            </div>
                                            <span className={cn(
                                                "text-[11px] font-semibold mt-0.5",
                                                currentPageName === item.page ? "text-terracotta-600" : "text-stone-500"
                                            )}>{item.name}</span>
                                        </>
                                    ) : (
                                        /* Regular tab items */
                                        <>
                                            <div className={cn(
                                                "w-11 h-11 rounded-xl flex items-center justify-center transition-all",
                                                currentPageName === item.page 
                                                    ? "bg-gradient-to-r from-terracotta-500 to-suede-600 shadow-md"
                                                    : "bg-transparent"
                                            )}>
                                                <item.icon className={cn(
                                                    "w-[24px] h-[24px]",
                                                    currentPageName === item.page ? "text-white" : "text-stone-500"
                                                )} />
                                            </div>
                                            <span className={cn(
                                                "text-[12px] font-medium mt-0.5",
                                                currentPageName === item.page ? "font-semibold text-terracotta-600" : "text-stone-500"
                                            )}>{item.name}</span>
                                        </>
                                    )}
                        </a>
                    ))}
                </div>
                </nav>
            </div>

            {/* AI Assistant - Only on Wardrobe page, hidden if dismissed */}
            {currentPageName === 'MyWardrobe' && !guideDismissed && <AIAssistant onDismiss={handleDismissAgent} />}

            {/* Restore AI Agents button — shows when any agent is dismissed, only on wardrobe */}
            {currentPageName === 'MyWardrobe' && anyAgentDismissed && (
                <div
                    className="fixed z-[45] flex justify-center"
                    style={{
                        bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
                        right: '16px'
                    }}
                >
                    <button
                        onClick={handleRestoreAgents}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-terracotta-600 to-suede-600 text-white text-sm font-medium rounded-full shadow-lg active:scale-95 transition-transform"
                        aria-label="Restore AI agents"
                    >
                        <Bot className="w-4 h-4" />
                        Show Agents
                    </button>
                </div>
            )}
        </div>
    );
}
